import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pause, Zap } from "lucide-react";
import {
  getDeploymentSummary,
  killDeployment,
  stopDeployment,
} from "../api/deployments";
import { apiErrorMessage } from "../lib/api";
import { formatINR } from "../lib/format";
import Button from "./Button";

export type AdminAction = "stop" | "kill";

export interface AdminActionTarget {
  id: string;
  name: string;
  mode: "paper" | "live";
  /** Who owns it — shown so the admin knows whose account is affected. */
  ownerLabel: string;
}

/** Confirmation for an admin stopping or killing ANOTHER user's deployment.
 *
 *  Stop leaves any open position with the owner's broker, unmanaged — so it
 *  warns loudly when there is one. Kill squares positions off through the
 *  owner's broker account; on a live deployment the admin must type the
 *  deployment name. The owner is told either way (Telegram + activity feed).
 */
export default function AdminStopKillDialog({
  target,
  action,
  onClose,
}: {
  target: AdminActionTarget;
  action: AdminAction;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [typed, setTyped] = useState("");

  const { data: summary, isLoading } = useQuery({
    queryKey: ["deployment-summary", target.id],
    queryFn: () => getDeploymentSummary(target.id),
    refetchInterval: 2_000,
  });
  const open = (summary?.positions ?? []).filter((p) => Number(p.quantity) !== 0);

  const run = useMutation({
    mutationFn: () =>
      action === "kill" ? killDeployment(target.id) : stopDeployment(target.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deployment", target.id] });
      qc.invalidateQueries({ queryKey: ["admin-user-deployments"] });
      qc.invalidateQueries({ queryKey: ["admin-user-summary"] });
      qc.invalidateQueries({ queryKey: ["admin-live"] });
      onClose();
    },
  });

  const isKill = action === "kill";
  const needsTyping = isKill && target.mode === "live";
  const canConfirm =
    !run.isPending && !isLoading && (!needsTyping || typed.trim() === target.name);

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center px-4 pt-24 bg-black/40"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-surface border border-border rounded-lg shadow-card w-full max-w-[480px] p-4 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-sm font-semibold text-fg">
          {isKill ? "Kill" : "Stop"} another user&apos;s deployment?
        </div>
        <div className="text-sm text-fg-subtle">
          <span className="font-medium text-fg">{target.name}</span> ({target.mode}) ·
          owned by <span className="font-medium text-fg">{target.ownerLabel}</span>
        </div>

        {isLoading ? (
          <div className="text-xs text-muted">Checking open positions…</div>
        ) : open.length > 0 ? (
          <div className="text-xs rounded border border-border bg-surface-2 px-3 py-2 space-y-0.5">
            <div className="font-medium text-fg">Open positions</div>
            {open.map((p) => (
              <div key={p.instrument_key} className="font-mono">
                {p.side} {p.quantity} {p.symbol} @ {formatINR(p.avg_price)} · unrealized{" "}
                {formatINR(p.unrealized_pnl, { sign: true })}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-muted">No open positions.</div>
        )}

        {isKill ? (
          <div className="text-xs text-down-700 dark:text-down-400 bg-down-50 dark:bg-down-500/10 border border-down-200 dark:border-down-400/30 rounded px-3 py-2">
            Kill blocks new orders, then{" "}
            <strong>squares off every open position</strong>
            {target.mode === "live"
              ? ` with real orders through ${target.ownerLabel}'s broker account`
              : " (paper)"}
            , and stops the deployment.
          </div>
        ) : open.length > 0 && target.mode === "live" ? (
          <div className="text-xs text-warn-700 dark:text-warn-400 bg-warn-50 dark:bg-warn-500/10 border border-warn-200 dark:border-warn-400/30 rounded px-3 py-2">
            <strong>Stop does NOT close positions.</strong> They stay open with{" "}
            {target.ownerLabel}&apos;s broker with nothing managing them — no target,
            no stop-loss. Use Kill to close them.
          </div>
        ) : (
          <div className="text-xs text-muted">
            Stop halts the deployment and leaves any open position with the broker.
          </div>
        )}

        {needsTyping && (
          <label className="block text-xs text-muted">
            Type <span className="font-mono text-fg">{target.name}</span> to confirm
            <input
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="mt-1 block w-full bg-bg border border-border rounded px-2 py-1.5 text-sm text-fg"
            />
          </label>
        )}

        <div className="text-xs text-muted">
          {target.ownerLabel} will be notified on Telegram (if set up) and in their
          activity feed. This is recorded in the audit log.
        </div>

        {run.isError && (
          <div className="text-xs text-down-700 dark:text-down-400">
            {apiErrorMessage(run.error)}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={isKill ? "danger" : "secondary"}
            disabled={!canConfirm}
            onClick={() => run.mutate()}
            iconLeft={isKill ? <Zap size={14} /> : <Pause size={14} />}
          >
            {run.isPending ? "Working…" : isKill ? "Kill deployment" : "Stop deployment"}
          </Button>
        </div>
      </div>
    </div>
  );
}
